import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BugDraft, BugFileResponse } from '@volley/crucible-ipc-types/ipc-channels';
import { useCrucibleClient } from '../transport/context';
import { projectQueryKey } from '../transport/project-query-key';

/**
 * React Query hooks around the three `bug:*` IPC channels (#246).
 *
 *  - `useBugDraft(gameId)` — polls `bug:get-draft` so the
 *    `<BugDraftCard>` mounts as soon as the SKILL drops a fresh
 *    `.foundry/bug-draft.json` and unmounts when main deletes it
 *    (success or cancel). Polling cadence mirrors
 *    `useInterviewStatus` from `useBuildGameInterview.ts` — short
 *    `staleTime` plus an explicit `refetchInterval` so a SKILL
 *    write surfaces in <2s without the renderer needing a real
 *    fs.watch.
 *
 *  - `useFileBug` / `useCancelBug` — imperative IPC wrappers used
 *    by the card's Send / Cancel buttons. Both invalidate the draft
 *    query on completion so a stale card disappears immediately
 *    rather than waiting for the next poll tick.
 */

const DRAFT_OPTIONS = {
  // Short staleTime + explicit refetchInterval: the SKILL writes
  // out of band, so React Query's freshness window has to be
  // smaller than the interval to keep poll-driven refetches honest.
  staleTime: 1500,
  refetchInterval: 2000,
  refetchOnMount: 'always',
  refetchOnWindowFocus: 'always',
  retry: false,
} as const;

export function useBugDraft(
  gameId: string | undefined,
  options: { enabled?: boolean } = {},
): {
  data: BugDraft | null | undefined;
  isLoading: boolean;
  refetch: () => void;
} {
  const client = useCrucibleClient();
  const query = useQuery<BugDraft | null>({
    queryKey: projectQueryKey(client, ['bug:get-draft', gameId]),
    queryFn: async () => {
      if (!gameId) throw new Error('gameId is required');
      return await client.invoke('bug:get-draft', { gameId });
    },
    enabled: (options.enabled ?? true) && typeof gameId === 'string' && gameId.length > 0,
    ...DRAFT_OPTIONS,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    refetch: () => {
      void query.refetch();
    },
  };
}

export function useFileBug(): {
  fileBug: (args: { gameId: string; repo: string; draftedAt: string }) => Promise<BugFileResponse>;
} {
  const client = useCrucibleClient();
  const queryClient = useQueryClient();
  return {
    fileBug: async (args) => {
      const result = await client.invoke('bug:file', args);
      // Invalidate so the card unmounts (or refreshes its error)
      // without waiting for the next 2s poll. On success the draft
      // file is gone and the next refetch returns null; on failure
      // the file survives and the card stays mounted.
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['bug:get-draft', args.gameId]),
      });
      return result;
    },
  };
}

export function useCancelBug(): {
  cancelBug: (gameId: string) => Promise<{ ok: boolean }>;
} {
  const client = useCrucibleClient();
  const queryClient = useQueryClient();
  return {
    cancelBug: async (gameId) => {
      const result = await client.invoke('bug:cancel', { gameId });
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['bug:get-draft', gameId]),
      });
      return result;
    },
  };
}
